/**
 * Web-only image prep before upload via compressorjs (EXIF orient, resize, compress).
 * Imported only from uploadFile.web.ts so native Metro builds are unaffected.
 */
import Compressor from 'compressorjs';
export const MAX_IMAGE_LONG_EDGE_PX = 1600;
/** Skip re-encode when the file is already small. */
export const SKIP_COMPRESS_UNDER_BYTES = 100 * 1024;
export const JPEG_QUALITY = 0.82;
const SKIP_MIME_TYPES = new Set(['image/svg+xml', 'image/gif']);
export function isCompressibleImage(file) {
    if (!(file instanceof File)) {
        return false;
    }
    if (!file.type || !file.type.startsWith('image/')) {
        return false;
    }
    return !SKIP_MIME_TYPES.has(file.type);
}
export function replaceFileExtension(filename, extension) {
    const trimmed = filename?.trim() || 'image';
    const base = trimmed.replace(/\.[^/.]+$/, '') || 'image';
    return `${base}.${extension}`;
}
export function extensionForMimeType(mimeType) {
    if (mimeType === 'image/png') {
        return 'png';
    }
    if (mimeType === 'image/webp') {
        return 'webp';
    }
    return 'jpg';
}
export function chooseOutputMimeType(hasAlpha) {
    return hasAlpha ? 'image/png' : 'image/jpeg';
}
/**
 * compressorjs has no alpha API — sample a downscaled bitmap for PNG/WebP only.
 */
export async function imageHasAlpha(file) {
    if (file.type !== 'image/png' && file.type !== 'image/webp') {
        return false;
    }
    let bitmap = null;
    try {
        bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        const width = Math.min(bitmap.width, 64);
        const height = Math.min(bitmap.height, 64);
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return false;
        }
        ctx.drawImage(bitmap, 0, 0, width, height);
        const { data } = ctx.getImageData(0, 0, width, height);
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) {
                return true;
            }
        }
        return false;
    }
    catch {
        return false;
    }
    finally {
        bitmap?.close();
    }
}
function ensureFileWithMatchingExtension(result, originalName) {
    const mimeType = result.type || 'image/jpeg';
    const filename = replaceFileExtension(result instanceof File ? result.name || originalName : originalName, extensionForMimeType(mimeType));
    if (result instanceof File && result.name === filename) {
        return result;
    }
    return new File([result], filename, {
        type: mimeType,
        lastModified: result instanceof File ? result.lastModified : Date.now(),
    });
}
async function compressWithCompressorjs(file) {
    const hasAlpha = await imageHasAlpha(file);
    const mimeType = chooseOutputMimeType(hasAlpha);
    return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-new -- compressorjs API is constructor + callbacks
        new Compressor(file, {
            maxWidth: MAX_IMAGE_LONG_EDGE_PX,
            maxHeight: MAX_IMAGE_LONG_EDGE_PX,
            quality: JPEG_QUALITY,
            checkOrientation: true,
            // Explicit mime: JPEG for opaque photos; PNG when transparency is present.
            // Disable size-based convertTypes so we don't strip alpha from large PNGs.
            mimeType,
            convertSize: Number.POSITIVE_INFINITY,
            success(result) {
                resolve(ensureFileWithMatchingExtension(result, file.name));
            },
            error(error) {
                reject(error);
            },
        });
    });
}
/**
 * Resize/compress a user image for upload. Returns the original File when
 * compression is skipped or fails.
 */
export default async function compressImageForUpload(file) {
    if (!isCompressibleImage(file)) {
        return file;
    }
    // Tiny files — leave alone (also preserves small transparent PNGs).
    if (file.size <= SKIP_COMPRESS_UNDER_BYTES) {
        return file;
    }
    try {
        const compressed = await compressWithCompressorjs(file);
        // Prefer original when the library did not shrink the payload.
        if (compressed.size >= file.size) {
            return file;
        }
        return compressed;
    }
    catch (error) {
        if (__DEV__) {
            console.warn('[compressImageForUpload] falling back to original', error);
        }
        return file;
    }
}
